import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Clock, 
  BookOpen, 
  PlayCircle,
  ChevronLeft,
  Users,
  Star,
  CheckCircle,
  Lock
} from 'lucide-react';

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // TODO: Fetch course details from API
    setLoading(false);
    // Simulated course data
    setCourse({
      id,
      title: "Advanced Sales Techniques",
      description: "Master the art of sales with proven techniques and strategies.",
      instructor: "Jane Smith",
      duration: "10h 30m",
      totalLessons: 12,
      enrolledStudents: 1234,
      rating: 4.8,
      thumbnail: "/api/placeholder/800/400",
      price: 99.99,
      level: "Intermediate",
      prerequisites: ["Basic Sales Knowledge", "Communication Skills"],
      sections: [
        {
          id: 1,
          title: "Introduction to Advanced Sales",
          lessons: [
            {
              id: 101,
              title: "Understanding Customer Psychology",
              duration: "45m",
              isComplete: true,
              isLocked: false
            },
            {
              id: 102,
              title: "Building Trust and Rapport",
              duration: "1h",
              isComplete: false,
              isLocked: false
            }
          ]
        },
        {
          id: 2,
          title: "Advanced Negotiation Tactics",
          lessons: [
            {
              id: 201,
              title: "Price Negotiation Strategies",
              duration: "1h 15m",
              isComplete: false,
              isLocked: true
            }
          ]
        }
      ]
    });
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Course Not Found</h2>
          <p className="text-gray-600 mb-4">The course you're looking for doesn't exist.</p>
          <button 
            onClick={() => navigate(-1)}
            className="text-blue-900 hover:underline"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const handleStartCourse = () => {
    navigate(`/courses/${id}/watch`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
          >
            <ChevronLeft size={20} />
            <span>Back to Courses</span>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{course.title}</h1>
              <p className="text-gray-600 mb-6">{course.description}</p>
              
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Clock size={16} />
                  {course.duration}
                </div>
                <div className="flex items-center gap-1">
                  <BookOpen size={16} />
                  {course.totalLessons} lessons
                </div>
                <div className="flex items-center gap-1">
                  <Users size={16} />
                  {course.enrolledStudents.toLocaleString()} students
                </div>
                <div className="flex items-center gap-1">
                  <Star size={16} className="text-yellow-400" />
                  {course.rating}
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-xl p-6">
                <img 
                  src={course.thumbnail} 
                  alt={course.title}
                  className="w-full aspect-video rounded-lg mb-4 object-cover"
                />
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Price</span>
                    <span className="text-2xl font-bold text-gray-900">
                      ${course.price}
                    </span>
                  </div>
                  
                  <button
                    onClick={handleStartCourse}
                    className="w-full bg-blue-900 text-white py-3 px-4 rounded-lg hover:bg-blue-800 transition-colors flex items-center justify-center gap-2"
                  >
                    <PlayCircle size={20} />
                    {progress > 0 ? 'Continue Learning' : 'Start Learning'}
                  </button>
                  
                  {progress > 0 && (
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Your Progress</span>
                        <span className="text-gray-900 font-medium">{progress}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full">
                        <div 
                          className="h-full bg-blue-900 rounded-full"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8 border-b border-gray-200 mb-8">
          {['overview', 'curriculum', 'reviews'].map((section) => (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              className={`pb-4 text-sm font-medium transition-colors relative capitalize ${
                activeSection === section
                  ? 'text-blue-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {section}
              {activeSection === section && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-900" />
              )}
            </button>
          ))}
        </div>

        {activeSection === 'curriculum' && (
          <div className="space-y-6">
            {course.sections.map((section) => (
              <div key={section.id} className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {section.title}
                </h3>
                
                <div className="space-y-3">
                  {section.lessons.map((lesson) => (
                    <button
                      key={lesson.id}
                      onClick={() => !lesson.isLocked && navigate(`/courses/${id}/watch?lesson=${lesson.id}`)}
                      disabled={lesson.isLocked}
                      className={`w-full flex items-center justify-between p-4 rounded-lg ${
                        lesson.isLocked
                          ? 'bg-gray-50 cursor-not-allowed'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {lesson.isComplete ? (
                          <CheckCircle size={20} className="text-green-500" />
                        ) : lesson.isLocked ? (
                          <Lock size={20} className="text-gray-400" />
                        ) : (
                          <PlayCircle size={20} className="text-blue-900" />
                        )}
                        <span className={`${lesson.isLocked ? 'text-gray-400' : 'text-gray-900'}`}>
                          {lesson.title}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500">{lesson.duration}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDetail;